import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Settings, Lock, CheckCircle, AlertCircle, ArrowLeft, Eye, EyeOff,
  Shield, User, Bell
} from 'lucide-react';
import clientPortalService from '../../services/clientPortalService';

const PortalSettings = () => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [formData, setFormData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const [prefs, setPrefs] = useState(null);
  const [prefsLoading, setPrefsLoading] = useState(true);
  const [prefsSaving, setPrefsSaving] = useState(false);

  useEffect(() => { loadPreferences(); }, []);

  const loadPreferences = async () => {
    try {
      const data = await clientPortalService.getNotificationPreferences();
      setPrefs(data);
    } catch (err) {
      console.error('Failed to load preferences:', err);
    } finally {
      setPrefsLoading(false);
    }
  };

  const togglePref = async (key) => {
    const prev = { ...prefs };
    const updated = { ...prefs, [key]: !prefs[key] };
    setPrefs(updated);
    try {
      setPrefsSaving(true);
      await clientPortalService.updateNotificationPreferences({ [key]: updated[key] });
    } catch (err) {
      setPrefs(prev);
    } finally {
      setPrefsSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
    setSuccess(false);
  };

  const validateForm = () => {
    if (!formData.current_password) { setError('Please enter your current password'); return false; }
    if (!formData.new_password) { setError('Please enter a new password'); return false; }
    if (formData.new_password.length < 8) { setError('New password must be at least 8 characters'); return false; }
    if (formData.new_password !== formData.confirm_password) { setError('New passwords do not match'); return false; }
    if (formData.current_password === formData.new_password) { setError('New password must be different from current password'); return false; }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess(false);
    if (!validateForm()) return;
    try {
      setSaving(true);
      await clientPortalService.changePassword(formData.current_password, formData.new_password);
      setSuccess(true);
      setFormData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-virgo-navy-900 via-virgo-navy-800 to-virgo-navy-900 py-12 px-4">
      <div className="max-w-xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 mb-4">
            <Settings className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white">Account Settings</h1>
          <p className="text-virgo-teal-400 mt-2">Manage your account security</p>
        </div>

        {/* Back Link */}
        <div className="mb-6">
          <Link to="/portal/dashboard" className="inline-flex items-center gap-2 text-virgo-teal-400 hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Dashboard
          </Link>
        </div>

        {/* ======== Profile Link ======== */}
        <Link
          to="/portal/profile-setup"
          className="block bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-6 mb-6 hover:border-cyan-500/50 transition-all group"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <User className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Edit Profile</h2>
                <p className="text-sm text-virgo-teal-500">Update your name, phone, and address</p>
              </div>
            </div>
            <ArrowLeft className="w-5 h-5 text-virgo-teal-500 rotate-180 group-hover:text-cyan-400 transition-colors" />
          </div>
        </Link>

        {/* ======== Login Security Section ======== */}
        <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-8 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-emerald-500/20 rounded-lg">
              <Shield className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white">Login Security</h2>
              <p className="text-xs text-virgo-teal-500">Your account is protected by email verification</p>
            </div>
            <span className="ml-auto px-3 py-1 rounded-full text-xs font-semibold bg-green-500/20 text-green-300 border border-green-500/30">
              Active
            </span>
          </div>
          <p className="text-virgo-teal-300 text-sm">
            A verification code is sent to your email each time you log in. This protects your account even if your password is compromised.
          </p>
        </div>

        {/* ======== Notification Preferences ======== */}
        <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-8 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-amber-500/20 rounded-lg">
              <Bell className="w-5 h-5 text-amber-400" />
            </div>
            <h2 className="text-xl font-semibold text-white">Email Notifications</h2>
          </div>
          {prefsLoading ? (
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-cyan-500" />
            </div>
          ) : prefs ? (
            <div className="space-y-4">
              {[
                { key: 'email_new_message', label: 'New Messages', desc: 'When your attorney sends you a message' },
                { key: 'email_document_shared', label: 'Documents Shared', desc: 'When a document is shared with you' },
                { key: 'email_invoice_ready', label: 'Invoice Ready', desc: 'When a new invoice is available' },
                { key: 'email_payment_confirmed', label: 'Payment Confirmed', desc: 'When your payment is processed' },
                { key: 'email_appointment_reminder', label: 'Appointment Reminders', desc: 'Reminders for upcoming appointments' },
              ].map(({ key, label, desc }) => (
                <div key={key} className="flex items-center justify-between py-2">
                  <div>
                    <p className="text-white font-medium">{label}</p>
                    <p className="text-virgo-teal-500 text-xs">{desc}</p>
                  </div>
                  <button
                    onClick={() => togglePref(key)}
                    disabled={prefsSaving}
                    className={`relative w-11 h-6 rounded-full transition-colors ${prefs[key] ? 'bg-cyan-500' : 'bg-virgo-navy-600 border border-virgo-teal-700/50'}`}
                  >
                    <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${prefs[key] ? 'translate-x-5' : ''}`} />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-virgo-teal-500 text-sm">Unable to load preferences</p>
          )}
        </div>

        {/* ======== Password Change Section ======== */}
        <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-cyan-500/20 rounded-lg">
              <Lock className="w-5 h-5 text-cyan-400" />
            </div>
            <h2 className="text-xl font-semibold text-white">Change Password</h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                <span className="text-red-300">{error}</span>
              </div>
            )}
            {success && (
              <div className="bg-green-500/20 border border-green-500/50 rounded-lg p-4 flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                <span className="text-green-300">Password changed successfully!</span>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-virgo-teal-300 mb-2">Current Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-virgo-teal-500" />
                <input type={showCurrentPassword ? 'text' : 'password'} name="current_password" value={formData.current_password} onChange={handleChange}
                  className="w-full pl-10 pr-12 py-3 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  placeholder="Enter current password" autoComplete="current-password" />
                <button type="button" onClick={() => setShowCurrentPassword(!showCurrentPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-virgo-teal-500 hover:text-virgo-teal-300">
                  {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-virgo-teal-300 mb-2">New Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-virgo-teal-500" />
                <input type={showNewPassword ? 'text' : 'password'} name="new_password" value={formData.new_password} onChange={handleChange}
                  className="w-full pl-10 pr-12 py-3 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  placeholder="Enter new password" autoComplete="new-password" />
                <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-virgo-teal-500 hover:text-virgo-teal-300">
                  {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="text-xs text-virgo-teal-500 mt-1">Must be at least 8 characters</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-virgo-teal-300 mb-2">Confirm New Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-virgo-teal-500" />
                <input type={showConfirmPassword ? 'text' : 'password'} name="confirm_password" value={formData.confirm_password} onChange={handleChange}
                  className="w-full pl-10 pr-12 py-3 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  placeholder="Confirm new password" autoComplete="new-password" />
                <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-virgo-teal-500 hover:text-virgo-teal-300">
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button type="submit" disabled={saving}
              className="w-full py-4 bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-semibold rounded-lg hover:from-cyan-600 hover:to-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-cyan-500/30 flex items-center justify-center gap-2">
              {saving ? (
                <><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div> Changing Password...</>
              ) : (
                <><CheckCircle className="w-5 h-5" /> Change Password</>
              )}
            </button>
          </form>
        </div>

        <div className="text-center mt-6 text-virgo-teal-500 text-sm">
          <p>Keep your password secure and don't share it with anyone.</p>
        </div>
      </div>
    </div>
  );
};

export default PortalSettings;